import clsx from 'clsx'
import { Button, ButtonProps } from '../Button'
import { ButtonGroup } from '../TextAndContent/ButtonGroup'
import { Card, CardContent } from '../ui/card'
import { Check } from 'lucide-react'
import React from 'react'
import { useSpacing, Spacing } from '../hooks/useSpacing'
import { TextComponent, TextComponentProps } from '../TextComponent'

export interface PricingFeature {
  content: { content: string; id: string }
  special?: boolean
  indented?: boolean
}

export interface PricingTableCard {
  featuredCard: boolean
  tierLabel: string
  bigTextPricing: string
  term: string
  tierDescription: string
  features: PricingFeature[]
  button: ButtonProps[]
  textOrPrice: boolean
  subcontentHeader: string
  disclaimer: string
}

export interface PricingTableProps {
  cardlist: PricingTableCard[]
  spacing: Spacing[]
  titleSection: TextComponentProps[]
}

const buildPricingCards = (cardlist: PricingTableCard[]) =>
  cardlist.map(card => (
    <Card
      key={card.tierLabel}
      className={`mx-auto h-fit w-full overflow-hidden rounded-lg bg-transparent md:max-w-[634px] ${card.featuredCard ? '[background:linear-gradient(94deg,rgba(8,19,43,1)_0%,rgba(36,70,127,1)_100%)]' : 'bg-white'}`}
    >
      <CardContent className={`flex flex-col gap-6 px-6 py-8`}>
        <div
          className={`bg-gradient-to-br ${card.featuredCard ? 'from-[#DEE897] to-[#CCDE44]' : 'from-blue-700 to-[#3C70C7]'} bg-clip-text text-[20px] font-medium leading-5 text-transparent`}
        >
          {card.tierLabel}
        </div>

        <div className="flex flex-col gap-2 items-start">
          <div className="flex items-start gap-0.5">
            <span
              className={`text-2xl text-gray-500 ${card.textOrPrice ? '' : 'hidden'}`}
            >
              $
            </span>
            <span
              className={`text-5xl font-bold leading-[48px] ${card.featuredCard ? 'text-white' : 'text-gray-900'}`}
            >
              {card.bigTextPricing}
            </span>
            <span
              className={`mb-1 self-end text-2xl text-gray-500 ${card.textOrPrice ? '' : 'hidden'}`}
            >
              {card.term}
            </span>
          </div>
          <div
            className={clsx(
              'text-base leading-6',
              card.featuredCard ? 'text-gray-400' : 'text-gray-600',
            )}
          >
            {card.tierDescription}
          </div>
        </div>

        <div className="w-full">
          <ButtonGroup buttons={card.button} />
        </div>

        {(card.subcontentHeader || card.features.length > 0 || card.disclaimer) && (
          <div className="flex flex-col gap-3">
            {card.subcontentHeader && (
              <div className="flex flex-col gap-2">
                <h3
                  className={`text-lg font-semibold ${card.featuredCard ? 'text-white' : 'text-gray-900'}`}
                >
                  {card.subcontentHeader}
                </h3>
              </div>
            )}

            {card.features.length > 0 && (
              <div className="flex flex-col gap-2">
                {card.features?.map((feature, index) => (
                  <div
                    key={feature.content.id || index}
                    className={`flex items-start gap-1.5 ${feature.indented ? 'ml-4' : ''}`}
                  >
                    {!feature.special ? (
                      <>
                        <div className="flex h-7 w-[21px] items-center justify-center">
                          <Check
                            className={`h-6 w-6 ${card.featuredCard ? 'text-white' : 'text-gray-700'}`}
                          />
                        </div>
                        <p
                          className={`text-lg font-normal leading-7 ${card.featuredCard ? 'text-gray-400' : 'text-gray-700'}`}
                        >
                          {feature.content.content || ''}
                        </p>
                      </>
                    ) : (
                      <>
                        <img
                          src="https://www.datocms-assets.com/114273/1727250139-provlabsfooter.svg"
                          className="my-auto w-6 h-6 text-white"
                          alt="ProvLabs logo"
                        />
                        <div className="flex-1">
                          <p
                            className={`text-lg font-bold leading-7 ${card.featuredCard ? 'text-white' : 'text-gray-900'}`}
                          >
                            {feature.content.content || ''}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
            {card.disclaimer && (
              <div className="text-sm italic font-normal text-gray-400">
                {card.disclaimer}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  ))

export const PricingTable = ({ data }: { data: PricingTableProps }) => {
  const spacing = useSpacing(data.spacing?.[0] as Spacing)

  return (
    <div
      className={clsx(
        'flex flex-col gap-2 px-4 mx-auto max-w-page md:px-16 lg:px-20 xl:px-24 md:flex-row md:items-center md:justify-center md:gap-0',
        spacing,
      )}
    >
      <div className="w-full">
        <TextComponent data={data.titleSection} />
        <div className="flex flex-col gap-4 lg:gap-0 lg:items-center lg:flex-row">
          {buildPricingCards(data.cardlist)}
        </div>
      </div>
    </div>
  )
}
